import { useRef } from 'react';
import { T, useGT } from 'gt-react';
import { PlusIcon, SparkleIcon, TrashIcon } from '@phosphor-icons/react';
import type {
  DataAssetResponse,
  StatefulTableDefinitionResponse,
} from '@erp/rpc';
import { WizardField } from 'src/components/ui/wizard';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { Spinner } from 'src/components/ui/spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { DATA_ASSET_ACCEPT } from '../data-assets/upload';
import {
  STATEFUL_COLUMN_TYPES,
  type ColumnDraft,
  type StatefulColumnType,
} from './columnDrafts';
import { keySelectionLosesRows, type KeyUniqueness } from './keyUniqueness';
import { isCandidateSelected, type KeyCandidate } from './suggestKeyColumns';

/**
 * The four step bodies of the create-a-data-mapping wizard. The wizard owns all
 * state; these are presentational, so each step reads as one screen instead of
 * one more branch in a 900-line component.
 */

export type SourceMode = 'upload' | 'existing' | 'none';
export type TableMode = 'new' | 'existing';

/** A source file, once one exists — however it got there. */
export interface WizardSource {
  dataAssetId: string;
  dataAssetVersionId: string;
  contentType: string;
  fileName: string;
}

export function ModeButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

export function SourcePanel({
  sourceMode,
  onSourceModeChange,
  file,
  onFileChange,
  assetName,
  onAssetNameChange,
  existingAssetId,
  onExistingAssetIdChange,
  readyAssets,
  source,
  uploading,
  uploadError,
}: {
  sourceMode: SourceMode;
  onSourceModeChange: (mode: SourceMode) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  assetName: string;
  onAssetNameChange: (name: string) => void;
  existingAssetId: string;
  onExistingAssetIdChange: (id: string) => void;
  readyAssets: DataAssetResponse[];
  source: WizardSource | null;
  uploading: boolean;
  uploadError: string | null;
}) {
  const gt = useGT();

  return (
    <>
      <div className="flex gap-2 mb-4">
        <ModeButton
          active={sourceMode === 'upload'}
          onClick={() => onSourceModeChange('upload')}
          label={gt('Upload a file')}
        />
        <ModeButton
          active={sourceMode === 'existing'}
          onClick={() => onSourceModeChange('existing')}
          label={gt('Use an existing asset')}
        />
        <ModeButton
          active={sourceMode === 'none'}
          onClick={() => onSourceModeChange('none')}
          label={gt('No source')}
        />
      </div>

      {sourceMode === 'upload' && (
        <UploadSourceFields
          file={file}
          onFileChange={onFileChange}
          assetName={assetName}
          onAssetNameChange={onAssetNameChange}
          uploadedFileName={source?.fileName}
          uploading={uploading}
          uploadError={uploadError}
        />
      )}

      {sourceMode === 'existing' && (
        <ExistingAssetField
          existingAssetId={existingAssetId}
          onExistingAssetIdChange={onExistingAssetIdChange}
          readyAssets={readyAssets}
        />
      )}

      {sourceMode === 'none' && (
        <p className="text-sm text-muted-foreground">
          <T context="explanation when a data mapping is created without a source file">
            The table starts empty. A workflow step, or a later upload, can fill
            it.
          </T>
        </p>
      )}
    </>
  );
}

function UploadSourceFields({
  file,
  onFileChange,
  assetName,
  onAssetNameChange,
  uploadedFileName,
  uploading,
  uploadError,
}: {
  file: File | null;
  onFileChange: (file: File | null) => void;
  assetName: string;
  onAssetNameChange: (name: string) => void;
  uploadedFileName?: string;
  uploading: boolean;
  uploadError: string | null;
}) {
  const gt = useGT();
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <WizardField
        name="file"
        label={gt('File')}
        hint={gt(
          'CSV, JSON, or a spreadsheet — spreadsheets are converted to CSV before upload.',
        )}
      >
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={DATA_ASSET_ACCEPT}
            className="hidden"
            onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            <T context="button to pick a file for a data mapping source">
              Choose file
            </T>
          </Button>
          <span className="text-sm text-muted-foreground truncate">
            {file?.name ?? uploadedFileName ?? gt('No file selected')}
          </span>
        </div>
      </WizardField>
      <WizardField name="assetName" label={gt('Asset name')}>
        <Input
          value={assetName}
          onChange={(event) => onAssetNameChange(event.target.value)}
          placeholder={gt('Planner codes')}
        />
      </WizardField>
      {uploading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner size="sm" />
          <T context="status while a data mapping source file uploads">
            Uploading…
          </T>
        </div>
      )}
      {uploadError && (
        <AlertContainer variant="destructive">
          <AlertDescription>{uploadError}</AlertDescription>
        </AlertContainer>
      )}
    </>
  );
}

function ExistingAssetField({
  existingAssetId,
  onExistingAssetIdChange,
  readyAssets,
}: {
  existingAssetId: string;
  onExistingAssetIdChange: (id: string) => void;
  readyAssets: DataAssetResponse[];
}) {
  const gt = useGT();

  return (
    <WizardField name="existingAssetId" label={gt('Data asset')}>
      <Select value={existingAssetId} onValueChange={onExistingAssetIdChange}>
        <SelectTrigger>
          <SelectValue placeholder={gt('Select a data asset')} />
        </SelectTrigger>
        <SelectContent>
          {readyAssets.map((asset) => (
            <SelectItem key={asset.id} value={asset.id}>
              {asset.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </WizardField>
  );
}

export function ColumnRow({
  column,
  isKey,
  error,
  onChange,
  onMakeKey,
  onRemove,
}: {
  column: ColumnDraft;
  isKey: boolean;
  error?: string;
  onChange: (patch: Partial<ColumnDraft>) => void;
  onMakeKey: () => void;
  onRemove?: () => void;
}) {
  const gt = useGT();

  return (
    <div className="rounded-md border p-2 space-y-2">
      <div className="flex items-center gap-2">
        <Input
          value={column.name}
          onChange={(event) => onChange({ name: event.target.value })}
          placeholder={gt('column_name')}
          className="max-w-[220px]"
        />
        <Select
          value={column.type}
          onValueChange={(value) =>
            onChange({ type: value as StatefulColumnType })
          }
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATEFUL_COLUMN_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="sm"
          variant={isKey ? 'default' : 'outline'}
          onClick={onMakeKey}
        >
          <T context="button marking a data mapping column as the lookup key">
            Key
          </T>
        </Button>
        {onRemove && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={onRemove}
            aria-label={gt('Remove column')}
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        )}
      </div>
      <Input
        value={column.description}
        onChange={(event) => onChange({ description: event.target.value })}
        placeholder={gt('What this column holds — the matcher reads this.')}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function SchemaPanel({
  tableMode,
  columns,
  keyColumnIds,
  columnErrors,
  canInfer,
  suggesting,
  suggestError,
  suggestNotes,
  onInfer,
  onAddColumn,
  onChangeColumn,
  onRemoveColumn,
  onMakeKey,
  keyCandidates,
  onApplyKeyCandidate,
  keyAnalysis,
}: {
  tableMode: TableMode;
  columns: ColumnDraft[];
  keyColumnIds: string[];
  columnErrors: Record<string, string>;
  canInfer: boolean;
  suggesting: boolean;
  suggestError: string | null;
  suggestNotes: string[];
  onInfer: () => void;
  onAddColumn: () => void;
  onChangeColumn: (id: string, patch: Partial<ColumnDraft>) => void;
  onRemoveColumn: (id: string) => void;
  onMakeKey: (id: string) => void;
  keyCandidates: KeyCandidate[];
  onApplyKeyCandidate: (candidate: KeyCandidate) => void;
  keyAnalysis: KeyUniqueness | null;
}) {
  // Built once rather than scanned per row, so the key check is constant-time.
  const keyColumnIdSet = new Set(keyColumnIds);

  if (tableMode === 'existing') {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="note shown when the schema comes from an existing stateful table">
          The columns come from the stateful table you pick on the next step.
        </T>
      </p>
    );
  }

  return (
    <>
      <SchemaPanelToolbar
        canInfer={canInfer}
        suggesting={suggesting}
        onInfer={onInfer}
        onAddColumn={onAddColumn}
      />
      <InferenceFeedback error={suggestError} notes={suggestNotes} />

      <div className="space-y-2 mt-3">
        {columns.map((column) => (
          <ColumnRow
            key={column.id}
            column={column}
            isKey={keyColumnIdSet.has(column.id)}
            error={columnErrors[column.id]}
            onChange={(patch) => onChangeColumn(column.id, patch)}
            onMakeKey={() => onMakeKey(column.id)}
            onRemove={
              columns.length > 1 ? () => onRemoveColumn(column.id) : undefined
            }
          />
        ))}
      </div>
      <KeyCandidateOptions
        candidates={keyCandidates}
        keyColumnIds={keyColumnIds}
        onApply={onApplyKeyCandidate}
      />
      <KeyUniquenessNotice analysis={keyAnalysis} />
      <p className="text-xs text-muted-foreground mt-2">
        <T context="hint explaining the key columns of a data mapping">
          The key is what a lookup resolves by, and the table keeps one row per
          key. Pick several columns when no single one is unique — the key
          becomes the combination.
        </T>
      </p>
    </>
  );
}

/** Column-list header: infer from the source file, or add a column by hand. */
function SchemaPanelToolbar({
  canInfer,
  suggesting,
  onInfer,
  onAddColumn,
}: {
  canInfer: boolean;
  suggesting: boolean;
  onInfer: () => void;
  onAddColumn: () => void;
}) {
  return (
    <div className="flex items-center justify-between mb-3">
      <Label>
        <T context="label for the column list of a new data mapping">Columns</T>
      </Label>
      <div className="flex items-center gap-2">
        {canInfer && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onInfer}
            disabled={suggesting}
          >
            {suggesting ? (
              <Spinner size="sm" className="mr-1.5" />
            ) : (
              <SparkleIcon className="w-4 h-4 mr-1.5" />
            )}
            <T context="button to infer columns from the uploaded source file">
              Infer from file
            </T>
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={onAddColumn}>
          <PlusIcon className="w-4 h-4 mr-1.5" />
          <T context="button to add a column to a data mapping">Add column</T>
        </Button>
      </div>
    </div>
  );
}

/** What the last inference had to say: the failure, or the caveats. */
function InferenceFeedback({
  error,
  notes,
}: {
  error: string | null;
  notes: string[];
}) {
  return (
    <>
      {error && (
        <AlertContainer variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </AlertContainer>
      )}
      {notes.length > 0 && (
        <AlertContainer>
          <AlertDescription>
            <ul className="list-disc pl-4 space-y-1">
              {notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </AlertDescription>
        </AlertContainer>
      )}
    </>
  );
}

/**
 * Keys the sampled rows prove unique, as one-click options. Inference already
 * applied the first one, so this is the list of alternatives — a file where the
 * SKU is unique but so is (order, line) lets the user take the one that matches
 * how the data will be looked up, without discovering the choice by trial.
 */
function KeyCandidateOptions({
  candidates,
  keyColumnIds,
  onApply,
}: {
  candidates: KeyCandidate[];
  keyColumnIds: string[];
  onApply: (candidate: KeyCandidate) => void;
}) {
  if (candidates.length === 0) return null;

  return (
    <div className="mt-3">
      <p className="text-xs text-muted-foreground mb-1.5">
        <T context="label above suggested key column combinations found in the source file">
          Keys the file supports — each one identifies exactly one row in the
          sampled data:
        </T>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {candidates.map((candidate) => {
          const selected = isCandidateSelected(candidate, keyColumnIds);
          return (
            <Button
              key={candidate.columnIds.join('|')}
              type="button"
              size="sm"
              variant={selected ? 'default' : 'outline'}
              className="font-mono text-xs"
              onClick={() => onApply(candidate)}
              aria-pressed={selected}
            >
              {candidate.columnNames.join(' + ')}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * What the chosen key would do to the sampled rows. Silent when the key keeps
 * every row — the interesting case is a key that quietly merges or drops them,
 * which is what makes a 204-row file land as 10 rows.
 */
function KeyUniquenessNotice({ analysis }: { analysis: KeyUniqueness | null }) {
  const gt = useGT();
  if (!analysis) return null;

  if (analysis.unknownColumns.length > 0) {
    return (
      <AlertContainer className="mt-3">
        <AlertDescription>
          {gt(
            'Cannot check the key against the file: {names} is not a column in it.',
            { names: analysis.unknownColumns.join(', ') },
          )}
        </AlertDescription>
      </AlertContainer>
    );
  }

  if (!keySelectionLosesRows(analysis)) {
    return (
      <AlertContainer className="mt-3">
        <AlertDescription>
          {analysis.truncated
            ? gt('This key is unique across all {n} sampled rows.', {
                n: String(analysis.sampledRows),
              })
            : gt('This key is unique across all {n} rows in the file.', {
                n: String(analysis.sampledRows),
              })}
        </AlertDescription>
      </AlertContainer>
    );
  }

  return (
    <AlertContainer variant="destructive" className="mt-3">
      <AlertDescription>
        <p>
          {gt(
            'This key would store {kept} of {total} rows. Add a column to the key to keep them apart.',
            {
              kept: String(analysis.distinctKeys),
              total: String(analysis.sampledRows),
            },
          )}
        </p>
        <ul className="list-disc pl-4 mt-1 space-y-0.5">
          {analysis.duplicateRows > 0 && (
            <li>
              {gt('{n} rows repeat a key and would merge, last one winning.', {
                n: String(analysis.duplicateRows),
              })}
            </li>
          )}
          {analysis.incompleteKeyRows > 0 && (
            <li>
              {gt('{n} rows have a blank key part and would be dropped.', {
                n: String(analysis.incompleteKeyRows),
              })}
            </li>
          )}
          {analysis.truncated && (
            <li>{gt('Counted over the sampled rows, not the whole file.')}</li>
          )}
        </ul>
      </AlertDescription>
    </AlertContainer>
  );
}

export function TablePanel({
  tableMode,
  onTableModeChange,
  tableName,
  onTableNameChange,
  tableDescription,
  onTableDescriptionChange,
  existingTableId,
  onExistingTableIdChange,
  tables,
  columnCount,
  keyColumnNames,
  mappingName,
}: {
  tableMode: TableMode;
  onTableModeChange: (mode: TableMode) => void;
  tableName: string;
  onTableNameChange: (name: string) => void;
  tableDescription: string;
  onTableDescriptionChange: (description: string) => void;
  existingTableId: string;
  onExistingTableIdChange: (id: string) => void;
  tables: StatefulTableDefinitionResponse[];
  columnCount: number;
  keyColumnNames: string[];
  mappingName: string;
}) {
  const gt = useGT();
  const existingTable = tables.find((table) => table.id === existingTableId);

  return (
    <>
      <div className="flex gap-2 mb-4">
        <ModeButton
          active={tableMode === 'new'}
          onClick={() => onTableModeChange('new')}
          label={gt('New table')}
        />
        <ModeButton
          active={tableMode === 'existing'}
          onClick={() => onTableModeChange('existing')}
          label={gt('Existing table')}
        />
      </div>

      {tableMode === 'new' ? (
        <>
          <WizardField
            name="tableName"
            label={gt('Table name')}
            hint={gt('Follows the mapping name unless you change it.')}
          >
            <Input
              value={tableName}
              onChange={(event) => onTableNameChange(event.target.value)}
              placeholder={mappingName || gt('Planner codes')}
            />
          </WizardField>
          <WizardField
            name="tableDescription"
            label={gt('Description')}
            hint={gt('Optional.')}
          >
            <Textarea
              value={tableDescription}
              onChange={(event) => onTableDescriptionChange(event.target.value)}
              rows={2}
            />
          </WizardField>
          <p className="text-sm text-muted-foreground">
            {gt('{count} columns, keyed by {key}', {
              count: String(columnCount),
              key: keyColumnNames.join(', ') || '—',
            })}
          </p>
        </>
      ) : (
        <WizardField name="existingTableId" label={gt('Stateful table')}>
          <Select
            value={existingTableId}
            onValueChange={onExistingTableIdChange}
          >
            <SelectTrigger>
              <SelectValue placeholder={gt('Select a stateful table')} />
            </SelectTrigger>
            <SelectContent>
              {tables.map((table) => (
                <SelectItem key={table.id} value={table.id}>
                  {table.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {existingTable && (
            <p className="text-xs text-muted-foreground mt-2">
              {gt('{count} columns, keyed by {key}', {
                count: String(existingTable.currentVersion.columns.length),
                key: existingTable.currentVersion.keyColumns.join(', '),
              })}
            </p>
          )}
        </WizardField>
      )}
    </>
  );
}

/**
 * The mapping's name, with the AI naming path beside it. The file name already
 * filled the box, so the model's job here is usually a rename — a name for what
 * a person looks the mapping up by, now that the columns are known.
 */
function MappingNameField({
  mappingName,
  onMappingNameChange,
  hasSource,
  onSuggestName,
  canSuggestName,
  namingSuggestion,
  nameSuggestError,
}: {
  mappingName: string;
  onMappingNameChange: (name: string) => void;
  hasSource: boolean;
  onSuggestName: () => void;
  canSuggestName: boolean;
  namingSuggestion: boolean;
  nameSuggestError: string | null;
}) {
  const gt = useGT();

  return (
    <>
      <WizardField
        name="mappingName"
        label={gt('Name')}
        hint={
          hasSource
            ? gt('Defaulted from the file name. Ask for a better one below.')
            : undefined
        }
      >
        <div className="flex items-center gap-2">
          <Input
            value={mappingName}
            onChange={(event) => onMappingNameChange(event.target.value)}
            placeholder={gt('Planner code → warehouse')}
          />
          {canSuggestName && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onSuggestName}
              disabled={namingSuggestion}
            >
              {namingSuggestion ? (
                <Spinner size="sm" className="mr-1.5" />
              ) : (
                <SparkleIcon className="w-4 h-4 mr-1.5" />
              )}
              {mappingName.trim()
                ? gt('Rename with AI')
                : gt('Name it with AI')}
            </Button>
          )}
        </div>
      </WizardField>
      {nameSuggestError && (
        <AlertContainer variant="destructive">
          <AlertDescription>{nameSuggestError}</AlertDescription>
        </AlertContainer>
      )}
    </>
  );
}

export function MappingPanel({
  mappingName,
  onMappingNameChange,
  mappingSlug,
  onMappingSlugChange,
  mappingDescription,
  onMappingDescriptionChange,
  hasSource,
  syncNow,
  onSyncNowChange,
  submitError,
  onSuggestName,
  canSuggestName,
  namingSuggestion,
  nameSuggestError,
}: {
  mappingName: string;
  onMappingNameChange: (name: string) => void;
  mappingSlug: string;
  onMappingSlugChange: (slug: string) => void;
  mappingDescription: string;
  onMappingDescriptionChange: (description: string) => void;
  hasSource: boolean;
  syncNow: boolean;
  onSyncNowChange: (syncNow: boolean) => void;
  submitError: string | null;
  onSuggestName: () => void;
  canSuggestName: boolean;
  namingSuggestion: boolean;
  nameSuggestError: string | null;
}) {
  const gt = useGT();

  return (
    <>
      <MappingNameField
        mappingName={mappingName}
        onMappingNameChange={onMappingNameChange}
        hasSource={hasSource}
        onSuggestName={onSuggestName}
        canSuggestName={canSuggestName}
        namingSuggestion={namingSuggestion}
        nameSuggestError={nameSuggestError}
      />
      <WizardField
        name="mappingSlug"
        label={gt('Slug')}
        hint={gt('How code resolves this mapping. Keep it stable.')}
      >
        <Input
          value={mappingSlug}
          onChange={(event) => onMappingSlugChange(event.target.value)}
          placeholder={gt('planner-map')}
        />
      </WizardField>
      <WizardField
        name="mappingDescription"
        label={gt('Description')}
        hint={gt('Optional.')}
      >
        <Textarea
          value={mappingDescription}
          onChange={(event) => onMappingDescriptionChange(event.target.value)}
          rows={2}
        />
      </WizardField>

      {hasSource && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={syncNow}
            onChange={(event) => onSyncNowChange(event.target.checked)}
          />
          <T context="checkbox to push the source file into the table right after creating a data mapping">
            Push the source file into the table now
          </T>
        </label>
      )}

      {submitError && (
        <AlertContainer variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </AlertContainer>
      )}
    </>
  );
}
